import { config } from '../config';
import { authorizedFetch } from './auth';

/**
 * Label PDFs are fetched as blobs rather than linked directly: the endpoint needs
 * a bearer token, so a plain <iframe src> or <a href> would get a 401. The blob
 * URL is what the viewer renders.
 */

export type LabelFormat = 'A4' | 'A6';

/** Pulls shipment ids out of a tool result so the UI can offer the label. */
export function shipmentIdsFromToolResult(text: string): number[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return [];
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return [];
  }

  const record = parsed as Record<string, unknown>;
  const ids: number[] = [];

  if (typeof record['shipment_id'] === 'number') {
    ids.push(record['shipment_id']);
  }

  if (Array.isArray(record['shipment_ids'])) {
    for (const id of record['shipment_ids']) {
      if (typeof id === 'number') {
        ids.push(id);
      }
    }
  }

  return [...new Set(ids)].filter((id) => Number.isSafeInteger(id) && id > 0);
}

export class LabelNotReadyError extends Error {}

export async function fetchLabelBlobUrl(
  shipmentIds: number[],
  format: LabelFormat = 'A6',
): Promise<string> {
  const path = `${config().apiBase}/labels/${shipmentIds.join(';')}?format=${format}`;
  const response = await authorizedFetch(path, { headers: { Accept: 'application/pdf' } });

  if (response.status === 409) {
    throw new LabelNotReadyError(
      'Het label is nog niet gerenderd. Probeer het over een paar seconden opnieuw.',
    );
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      detail = body.error ?? detail;
    } catch {
      // Non-JSON error body; keep the status.
    }
    throw new Error(detail);
  }

  return URL.createObjectURL(await response.blob());
}
